/**
 * Sprint 39 — Conversión pesos ↔ centavos para evitar errores IEEE-754.
 *
 * Convención canónica del sistema tras la migración:
 *   - Storage (SQLite), services y API IPC: Int (centavos).
 *   - UI presentación (formatPrecio): convierte centavos → "$X.XX".
 *   - UI input: convierte pesos del input → centavos al enviar.
 *
 * Todas funciones puras, sin dependencias, testeable sin Electron.
 */

const MAX_CENTAVOS_SEGURO = Number.MAX_SAFE_INTEGER; // 9_007_199_254_740_991

/**
 * Convierte pesos (puede tener decimales) a centavos (Int).
 * Redondea para tolerar errores IEEE-754 en el input.
 * `12.5` → `1250`. `0.1 + 0.2` (= 0.30000000000000004) → `30`.
 *
 * Valores no-finitos o negativos extremos se convierten a 0 como
 * defensa contra inputs inválidos.
 */
export function pesosACentavos(pesos: number): number {
  if (!Number.isFinite(pesos)) return 0;
  // Math.round es media-arriba para positivos. Para negativos, redondea
  // también hacia +∞: -0.5 → 0. Ajustamos para mantener semántica de
  // "media-arriba en valor absoluto" si el sistema permitiera negativos.
  const c = Math.round(pesos * 100);
  if (Math.abs(c) > MAX_CENTAVOS_SEGURO) return 0;
  return c;
}

/**
 * Convierte centavos (Int) a pesos (number con 2 decimales).
 * `1250` → `12.5` (que JavaScript serializa como `12.5`, no `12.50`).
 *
 * Útil para llenar inputs de formulario en edit mode.
 */
export function centavosAPesos(centavos: number): number {
  if (!Number.isFinite(centavos)) return 0;
  // Dividir entre 100 puede dar IEEE-754 (ej: 1/3 * 100 = 33, /100 = 0.33).
  // Como solo lo usamos para mostrar en input, basta con la división simple.
  return centavos / 100;
}

/**
 * Suma una lista de centavos. Útil para acumulación en services.
 * Garantiza el resultado en Int sin error de IEEE-754.
 *
 * Si algún input no es Int, lo coerciona con `Math.round`.
 */
export function sumaCentavos(...montos: number[]): number {
  let total = 0;
  for (const m of montos) {
    if (!Number.isFinite(m)) continue;
    total += Math.round(m);
  }
  return total;
}

/**
 * Formatea centavos como string monetario localizado.
 * Default es-MX con símbolo "$".
 *
 * Ejemplos:
 *   formatCentavos(1250) → "$12.50"
 *   formatCentavos(0) → "$0.00"
 *   formatCentavos(123456) → "$1,234.56"
 *   formatCentavos(-50) → "-$0.50"
 *   formatCentavos(1250, "€", "es-ES") → "€12,50"
 */
export function formatCentavos(
  centavos: number,
  simbolo: string = '$',
  locale: string = 'es-MX',
): string {
  if (!Number.isFinite(centavos)) return `${simbolo}0.00`;
  const pesos = centavos / 100;
  const negativo = pesos < 0;
  const absStr = Math.abs(pesos).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${negativo ? '-' : ''}${simbolo}${absStr}`;
}

/**
 * Verifica si un número es un Int seguro para usar como centavos.
 * Útil para validación Zod con `.refine(esEnteroSeguro)`.
 */
export function esEnteroSeguro(n: number): boolean {
  return (
    Number.isFinite(n) &&
    Number.isInteger(n) &&
    Math.abs(n) <= MAX_CENTAVOS_SEGURO
  );
}
